/**
 * DeleteFieldConfirmation - Field-specific confirmation dialog
 *
 * Story 3.5: Implement Soft Delete Confirmation Dialog
 * AC-3.10: Shows message with row count: "This column contains data in X rows."
 *
 * Uses the generic ConfirmationDialog with field-specific messaging.
 */

import React from 'react';
import { ConfirmationDialog } from './ConfirmationDialog';

export interface DeleteFieldConfirmationProps {
  /** Name of the field being deleted */
  fieldName: string;
  /** Number of rows that have data in this field */
  rowCount: number;
  /** Callback when user confirms deletion */
  onConfirm: () => void;
  /** Callback when user cancels */
  onCancel: () => void;
  /** Whether dialog is open */
  isOpen?: boolean;
}

/**
 * DeleteFieldConfirmation Component
 *
 * AC-3.10: Displays confirmation dialog when deleting field with data:
 * - Warning icon
 * - Message: "This column contains data in X rows. Deleting it will permanently remove this data."
 * - "Cancel" button (default focus)
 * - "Delete Column" button (destructive styling)
 */
export function DeleteFieldConfirmation({
  fieldName,
  rowCount,
  onConfirm,
  onCancel,
  isOpen = true,
}: DeleteFieldConfirmationProps) {
  // Format plural/singular "row" based on count
  const rowText = rowCount === 1 ? 'row' : 'rows';

  return (
    <ConfirmationDialog
      title={`Delete "${fieldName}"?`}
      message={`This column contains data in ${rowCount} ${rowText}. Deleting it will permanently remove this data.`}
      confirmLabel="Delete Column"
      cancelLabel="Cancel"
      variant="destructive"
      onConfirm={onConfirm}
      onCancel={onCancel}
      isOpen={isOpen}
    />
  );
}

export default DeleteFieldConfirmation;
